import { CheckIcon, FunnelSimpleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export function ColumnHeaderFilter({
  title,
  options,
  selected,
  onChange,
  multiple = true,
  searchable = false,
}: {
  title: string;
  options: { label: string; value: string }[];
  selected: readonly string[];
  onChange: (values: string[]) => void;
  multiple?: boolean;
  searchable?: boolean;
}) {
  const gt = useGT();
  const selectedSet = new Set(selected);
  const isActive = selected.length > 0;

  function toggleValue(value: string) {
    if (multiple) {
      const next = new Set(selectedSet);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      onChange(Array.from(next));
      return;
    }
    onChange(selectedSet.has(value) ? [] : [value]);
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('h-8 w-8 p-0', isActive && 'text-primary bg-accent')}
          aria-label={gt('Filter {column}', {
            context: 'aria label for a table column header filter',
            column: title,
          })}
          onClick={(event) => event.stopPropagation()}
        >
          <FunnelSimpleIcon
            className="h-3.5 w-3.5"
            weight={isActive ? 'fill' : 'regular'}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          {searchable && <CommandInput placeholder={title} />}
          <CommandList>
            {searchable && (
              <CommandEmpty>
                <T context="empty state in column header filter dropdown">
                  No results found.
                </T>
              </CommandEmpty>
            )}
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedSet.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={`${option.label} ${option.value}`}
                    onSelect={() => toggleValue(option.value)}
                  >
                    <div
                      className={cn(
                        'flex size-4 items-center justify-center rounded-[4px] border',
                        isSelected
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'border-input [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="text-primary-foreground size-3.5" />
                    </div>
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {isActive && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onChange([])}
                    className="justify-center text-center"
                  >
                    <T context="option to clear a column header filter">
                      Clear filter
                    </T>
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
